import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings, X } from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import { useApi } from '../auth/useApi';

// Forest green palette (matches the prototype)
const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
  chart: ['#1e4d3a', '#7a9b7e', '#c4793a', '#4a7a4e', '#5a7b8a', '#a98f4b'],
};

const PRIORITY_COLORS: Record<string, string> = {
  Critical: '#c4533a',
  High: '#c4793a',
  Medium: '#7a9b7e',
  Low: '#a8a8a8',
};

// -------------------- Types --------------------
interface BucketRow { label: string; count: number; }
interface KPIs {
  Open: number | null;
  Resolved: number | null;
  Total: number;
  AvgRating: number | null;
}
interface WeeklyRow {
  WeekNo: number;
  WeekStart: string;
  Created: number;
  Resolved: number;
}
interface TechWorkloadRow {
  UserId: number;
  DisplayName: string;
  OpenCount: number;
  CriticalCount: number;
  HighCount: number;
}
interface DashboardData {
  byStatus: BucketRow[];
  byPriority: BucketRow[];
  byCategory: BucketRow[];
  kpis: KPIs;
  weekly: WeeklyRow[];
  techWorkload?: TechWorkloadRow[];
}

// Widget keys — these strings are stored in UserDashboardPrefs.WidgetsJson.
// Default is "show" for any missing key, so adding a new widget appears for
// all users until they explicitly hide it.
type WidgetKey = 'kpis' | 'byPriority' | 'byStatus' | 'weekly' | 'byCategory' | 'techWorkload';
type WidgetPrefs = Partial<Record<WidgetKey, boolean>>;

const WIDGET_DEFS: { key: WidgetKey; label: string; adminOnly?: boolean }[] = [
  { key: 'kpis',         label: 'KPI tiles (Open / Resolved / Total / Avg satisfaction)' },
  { key: 'byPriority',   label: 'By Priority (pie chart)' },
  { key: 'byStatus',     label: 'By Status (pie chart)' },
  { key: 'weekly',       label: 'Weekly Trend (line chart)' },
  { key: 'byCategory',   label: 'By Category (bar chart)' },
  { key: 'techWorkload', label: 'Tech Workload (admin only)', adminOnly: true },
];

const widgetVisible = (prefs: WidgetPrefs, key: WidgetKey): boolean => {
  // Default: visible unless explicitly false
  return prefs[key] !== false;
};

interface SavedChart {
  ChartId: number;
  Title: string;
  ChartType: 'line' | 'bar' | 'pie' | 'kpi';
  ConfigJson: string;  // serialized ChartConfig
  SortOrder: number;
}
interface ChartConfig {
  metric: string;
  dateFrom?: string;
  dateTo?: string;
  priorityFilter?: string;
  techFilter?: number;
}
type ChartData = { label: string; value: number; resolved?: number }[];

const METRIC_OPTIONS: { value: string; label: string; defaultChart: 'line' | 'bar' | 'pie' | 'kpi' }[] = [
  { value: 'tickets_by_priority',       label: 'Tickets by priority',        defaultChart: 'pie' },
  { value: 'tickets_by_status',         label: 'Tickets by status',          defaultChart: 'pie' },
  { value: 'tickets_by_category',       label: 'Tickets by category',        defaultChart: 'bar' },
  { value: 'tickets_by_tech',           label: 'Tickets by tech',            defaultChart: 'bar' },
  { value: 'tickets_by_requester',      label: 'Tickets by requester',       defaultChart: 'bar' },
  { value: 'tickets_by_source',         label: 'Tickets by source',          defaultChart: 'pie' },
  { value: 'tickets_by_form',           label: 'Tickets by form',            defaultChart: 'bar' },
  { value: 'top_tags',                  label: 'Top 10 tags',                defaultChart: 'bar' },
  { value: 'weekly_volume',             label: 'Weekly volume (created/resolved)', defaultChart: 'line' },
  { value: 'avg_resolution_minutes',    label: 'Avg resolution time (min)',  defaultChart: 'kpi' },
  { value: 'avg_first_response_minutes',label: 'Avg first response (min)',   defaultChart: 'kpi' },
  { value: 'sla_compliance_pct',        label: 'SLA compliance %',           defaultChart: 'kpi' },
  { value: 'avg_satisfaction',          label: 'Avg satisfaction (CSAT)',    defaultChart: 'bar' },
];

interface Me {
  userId: number;
  email: string;
  displayName: string;
  role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent';
}

interface TicketSummary {
  TicketId: number;
  Subject: string;
  Priority: string;
  StatusName?: string;
  CreatedAt: string;
  AssignedToUserId: number | null;
  CreatedByUserId: number;
  CreatedByName?: string;
}

// -------------------- Tiny UI primitives --------------------
// Lightweight responsive helper: true on narrow (phone) viewports.
function useIsNarrow(bp = 700): boolean {
  const [narrow, setNarrow] = useState<boolean>(typeof window !== 'undefined' && window.innerWidth <= bp);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth <= bp);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [bp]);
  return narrow;
}

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: 20, ...style,
  }}>{children}</div>
);

const StatCard: React.FC<{ label: string; value: React.ReactNode; color: string; to?: string }> = ({ label, value, color, to }) => {
  const inner = (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`, borderLeft: `4px solid ${color}`,
      borderRadius: 8, padding: '16px 18px',
      cursor: to ? 'pointer' : 'default',
      transition: 'box-shadow 0.15s, transform 0.1s',
    }}
    onMouseEnter={to ? e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; } : undefined}
    onMouseLeave={to ? e => { (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; } : undefined}
    >
      <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 28, color, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
  if (to) return <Link to={to} style={{ textDecoration: 'none' }}>{inner}</Link>;
  return inner;
};

const Badge: React.FC<{ children: React.ReactNode; color: string }> = ({ children, color }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    color, background: `${color}15`, border: `1px solid ${color}40`,
  }}>{children}</span>
);

// -------------------- Helpers --------------------
const fmtRelative = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

// -------------------- Dashboard --------------------
export const Dashboard: React.FC<{ me: Me }> = ({ me }) => {
  const api = useApi();
  const navigate = useNavigate();
  const narrow = useIsNarrow();
  const [data, setData] = useState<DashboardData | null>(null);
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [watching, setWatching] = useState<TicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-user widget visibility
  const [widgetPrefs, setWidgetPrefs] = useState<WidgetPrefs>({});
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Custom charts (saved per-user)
  const [savedCharts, setSavedCharts] = useState<SavedChart[]>([]);
  const [chartBuilderOpen, setChartBuilderOpen] = useState(false);
  const [editingChart, setEditingChart] = useState<SavedChart | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [d, t, prefs, charts, watching] = await Promise.all([
          api.get<DashboardData>('/api/reports/dashboard'),
          api.get<TicketSummary[]>('/api/tickets'),
          api.get<{ widgets: WidgetPrefs }>('/api/me/dashboard-prefs').catch(() => ({ widgets: {} })),
          api.get<SavedChart[]>('/api/reports/charts').catch(() => [] as SavedChart[]),
          api.get<TicketSummary[]>('/api/tickets/watching').catch(() => [] as TicketSummary[]),
        ]);
        if (!cancelled) {
          setData(d);
          setTickets(Array.isArray(t) ? t : []);
          setWidgetPrefs(prefs?.widgets ?? {});
          setSavedCharts(Array.isArray(charts) ? charts : []);
          setWatching(Array.isArray(watching) ? watching : []);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleWidget = async (key: WidgetKey) => {
    const next: WidgetPrefs = { ...widgetPrefs, [key]: !widgetVisible(widgetPrefs, key) };
    setWidgetPrefs(next);
    try {
      await api.put('/api/me/dashboard-prefs', { widgets: next });
    } catch (e) {
      console.warn('Failed to save dashboard prefs:', e);
    }
  };

  const reloadCharts = async () => {
    try {
      const charts = await api.get<SavedChart[]>('/api/reports/charts');
      setSavedCharts(Array.isArray(charts) ? charts : []);
    } catch { /* ignore */ }
  };

  const saveChart = async (title: string, chartType: 'line'|'bar'|'pie'|'kpi', config: ChartConfig) => {
    if (editingChart) {
      await api.patch(`/api/reports/charts/${editingChart.ChartId}`, { title, chartType, config });
    } else {
      await api.post('/api/reports/charts', { title, chartType, config });
    }
    setEditingChart(null);
    setChartBuilderOpen(false);
    await reloadCharts();
  };

  const deleteChart = async (chart: SavedChart) => {
    if (!window.confirm(`Delete chart "${chart.Title}"?`)) return;
    try {
      await api.del(`/api/reports/charts/${chart.ChartId}`);
      await reloadCharts();
    } catch (e: any) { alert(`Delete failed: ${e?.message}`); }
  };

  if (loading) {
    return <div style={{ padding: 40, color: C.textMuted }}>Loading dashboard…</div>;
  }
  if (error) {
    return <div style={{ padding: 40, color: C.danger }}>Failed to load dashboard: {error}</div>;
  }
  if (!data) return null;

  const firstName = me.displayName.split(' ')[0] || me.email;
  const role = me.role;

  // --- USER role: simple personal view ---
  if (role === 'user') {
    const myOpen = tickets.filter(t =>
      t.CreatedByUserId === me.userId &&
      t.StatusName !== 'Resolved' &&
      t.StatusName !== 'Closed'
    );
    return (
      <div>
        <h1 style={{ color: C.primary, fontSize: 26, margin: '0 0 6px' }}>Welcome, {firstName}</h1>
        <p style={{ color: C.textMuted, margin: '0 0 24px' }}>Here's a quick glance at your IT requests.</p>

        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
          <StatCard label="Open Tickets" value={data.kpis.Open ?? 0} color={C.primary} to="/tickets?status=open" />
          <StatCard label="Resolved" value={data.kpis.Resolved ?? 0} color={C.ok} to="/tickets?status=closed" />
          <StatCard label="Total Submitted" value={data.kpis.Total} color={C.secondary} to="/tickets" />
        </div>

        <Card>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Your Open Tickets</h3>
          {myOpen.length === 0 && <p style={{ color: C.textMuted, fontSize: 13 }}>No open tickets. ✨</p>}
          {myOpen.map(t => (
            <div key={t.TicketId}
              onClick={() => navigate(`/tickets/${t.TicketId}`)}
              style={{
                padding: '10px 8px', margin: '0 -8px', borderBottom: `1px solid ${C.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer', borderRadius: 6,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = C.bg; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div>
                <div style={{ fontWeight: 500 }}>#{t.TicketId} · {t.Subject}</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Created {fmtRelative(t.CreatedAt)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Badge color={PRIORITY_COLORS[t.Priority] ?? C.secondary}>{t.Priority}</Badge>
                <Badge color={C.secondary}>{t.StatusName ?? 'Open'}</Badge>
              </div>
            </div>
          ))}
        </Card>

        {watching.length > 0 && (
          <Card style={{ marginTop: 24 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Tickets You're Watching</h3>
            <p style={{ color: C.textMuted, fontSize: 12, margin: '0 0 12px' }}>
              Open tickets where you're a secondary contact.
            </p>
            {watching.map(t => (
              <div key={t.TicketId}
                onClick={() => navigate(`/tickets/${t.TicketId}`)}
                style={{
                  padding: '10px 8px', margin: '0 -8px', borderBottom: `1px solid ${C.border}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  cursor: 'pointer', borderRadius: 6,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = C.bg; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>#{t.TicketId} · {t.Subject}</div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                    From {t.CreatedByName ?? 'Unknown'} · {fmtRelative(t.CreatedAt)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Badge color={PRIORITY_COLORS[t.Priority] ?? C.secondary}>{t.Priority}</Badge>
                  <Badge color={C.secondary}>{t.StatusName ?? 'Open'}</Badge>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    );
  }

  // --- ADMIN / TECH / VIEWER: rich charts view ---
  // Click a chart element → open the tickets list filtered to that slice/bar.
  const drillTo = (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    navigate(`/tickets?${qs}`);
  };
  const pickName = (d: any): string | undefined => d?.name ?? d?.payload?.name;

  const priorityChartData = (data.byPriority ?? [])
    .filter(b => b.count > 0)
    .map(b => ({ name: b.label, value: b.count }));
  const statusChartData = (data.byStatus ?? [])
    .filter(b => b.count > 0)
    .map(b => ({ name: b.label, value: b.count }));
  const weeklyChartData = (data.weekly ?? [])
    .slice(0, 8)
    .reverse()
    .map(w => ({
      week: new Date(w.WeekStart).toLocaleDateString('en', { month: 'short', day: 'numeric' }),
      created: w.Created,
      resolved: w.Resolved,
    }));

  const headerTitle = (role === 'tech' || role === 'agent') ? `${firstName}'s Queue` : 'IT Dashboard';
  const headerSub = (role === 'tech' || role === 'agent')
    ? `${data.kpis.Open ?? 0} open · ${data.kpis.Resolved ?? 0} resolved`
    : `Society Brands IT Operations · ${new Date().toLocaleDateString('en', { dateStyle: 'full' })}`;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: C.primary, fontSize: 26, margin: '0 0 6px' }}>{headerTitle}</h1>
          <p style={{ color: C.textMuted, margin: 0 }}>{headerSub}</p>
        </div>
        <button
          onClick={() => setSettingsOpen(true)}
          title="Customize dashboard widgets"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: `1px solid ${C.border}`,
            color: C.textMuted, padding: '6px 12px', borderRadius: 4,
            cursor: 'pointer', fontSize: 13,
          }}
        >
          <Settings size={14} /> Customize
        </button>
      </div>

      {/* KPI tiles (clickable: drill down to filtered tickets list) */}
      {widgetVisible(widgetPrefs, 'kpis') && (
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          <StatCard label="Open" value={data.kpis.Open ?? 0} color={C.primary} to="/tickets?status=open" />
          <StatCard label="Resolved" value={data.kpis.Resolved ?? 0} color={C.ok} to="/tickets?status=closed" />
          <StatCard label="Total" value={data.kpis.Total} color={C.secondary} to="/tickets" />
          <StatCard
            label="Avg Satisfaction"
            value={data.kpis.AvgRating != null ? `${Number(data.kpis.AvgRating).toFixed(1)} / 5` : '—'}
            color={C.warn}
          />
        </div>
      )}

      {/* Charts row */}
      {(widgetVisible(widgetPrefs, 'byPriority') || widgetVisible(widgetPrefs, 'byStatus')) && (
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {widgetVisible(widgetPrefs, 'byPriority') && (
        <Card>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>By Priority</h3>
          {priorityChartData.length === 0 ? (
            <p style={{ color: C.textMuted, fontSize: 13 }}>No tickets yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={priorityChartData} dataKey="value" cx="50%" cy="50%"
                  innerRadius={50} outerRadius={80} paddingAngle={2}
                  style={{ cursor: 'pointer' }}
                  onClick={(d: any) => { const n = pickName(d); if (n) drillTo({ priority: n }); }}>
                  {priorityChartData.map((entry, i) => (
                    <Cell key={i} fill={PRIORITY_COLORS[entry.name] ?? C.chart[i % C.chart.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        )}

        {widgetVisible(widgetPrefs, 'byStatus') && (
        <Card>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>By Status</h3>
          {statusChartData.length === 0 ? (
            <p style={{ color: C.textMuted, fontSize: 13 }}>No tickets yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusChartData} dataKey="value" cx="50%" cy="50%"
                  innerRadius={50} outerRadius={80} paddingAngle={2}
                  style={{ cursor: 'pointer' }}
                  onClick={(d: any) => { const n = pickName(d); if (n) drillTo({ status: n }); }}>
                  {statusChartData.map((entry, i) => (
                    <Cell key={i} fill={C.chart[i % C.chart.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        )}
      </div>
      )}

      {/* Weekly trend */}
      {widgetVisible(widgetPrefs, 'weekly') && (
      <Card style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Weekly Trend</h3>
        {weeklyChartData.length === 0 ? (
          <p style={{ color: C.textMuted, fontSize: 13 }}>Not enough history yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={weeklyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="week" tick={{ fontSize: 12, fill: C.text }} />
              <YAxis tick={{ fontSize: 12, fill: C.text }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="created" stroke={C.primary} strokeWidth={2} name="Created" />
              <Line type="monotone" dataKey="resolved" stroke={C.ok} strokeWidth={2} name="Resolved" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>
      )}

      {/* By category bar */}
      {widgetVisible(widgetPrefs, 'byCategory') && data.byCategory && data.byCategory.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>By Category</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.byCategory.map(b => ({ name: b.label, count: b.count }))}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: C.text }} />
              <YAxis tick={{ fontSize: 12, fill: C.text }} />
              <Tooltip />
              <Bar dataKey="count" fill={C.primary} style={{ cursor: 'pointer' }}
                onClick={(d: any) => { const n = pickName(d); if (n) drillTo({ category: n }); }} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Tech workload — admin/viewer only */}
      {(role === 'admin' || role === 'viewer') && widgetVisible(widgetPrefs, 'techWorkload') && data.techWorkload && data.techWorkload.length > 0 && (
        <Card>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Tech Workload</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 4 }}>
            {/* Header row */}
            <div style={{
              display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 8,
              padding: '8px 4px', borderBottom: `1px solid ${C.border}`,
              fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px',
            }}>
              <div>Tech</div>
              <div style={{ textAlign: 'right' }}>Open</div>
              <div style={{ textAlign: 'right' }}>Critical</div>
              <div style={{ textAlign: 'right' }}>High</div>
            </div>
            {data.techWorkload.map(t => (
              <div key={t.UserId} style={{
                display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 8,
                padding: '10px 4px', borderBottom: `1px solid ${C.border}`,
                fontSize: 14, alignItems: 'center',
              }}>
                <div style={{ color: C.text, fontWeight: 500 }}>{t.DisplayName}</div>
                <div style={{ textAlign: 'right', color: t.OpenCount > 10 ? C.warn : C.text, fontWeight: t.OpenCount > 0 ? 600 : 400 }}>
                  {t.OpenCount}
                </div>
                <div style={{ textAlign: 'right', color: t.CriticalCount > 0 ? C.danger : C.textMuted, fontWeight: t.CriticalCount > 0 ? 600 : 400 }}>
                  {t.CriticalCount}
                </div>
                <div style={{ textAlign: 'right', color: t.HighCount > 0 ? C.warn : C.textMuted, fontWeight: t.HighCount > 0 ? 600 : 400 }}>
                  {t.HighCount}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Custom charts (saved per-user) — visible to everyone except plain users
          (plain users are handled by the early return above, so role here is admin/tech/viewer) */}
      <Card style={{ marginTop: 24 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 14, flexWrap: 'wrap', gap: 8,
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, color: C.primary }}>My charts</h3>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                Build and save custom charts. Only you see your charts.
              </div>
            </div>
            <button
              onClick={() => { setEditingChart(null); setChartBuilderOpen(true); }}
              style={{
                background: C.primary, color: 'white', border: 'none',
                padding: '7px 14px', borderRadius: 4, fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
              }}
            >+ New chart</button>
          </div>

          {savedCharts.length === 0 ? (
            <div style={{
              padding: 32, textAlign: 'center', color: C.textMuted, fontSize: 13,
              border: `1px dashed ${C.border}`, borderRadius: 6,
            }}>
              No saved charts yet. Click "+ New chart" to build one.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
              gap: 14,
            }}>
              {savedCharts.map(c => (
                <SavedChartCard
                  key={c.ChartId}
                  chart={c}
                  onEdit={() => { setEditingChart(c); setChartBuilderOpen(true); }}
                  onDelete={() => deleteChart(c)}
                />
              ))}
            </div>
          )}
        </Card>

      {/* Chart builder modal */}
      {chartBuilderOpen && (
        <ChartBuilderModal
          editing={editingChart}
          onSave={saveChart}
          onClose={() => { setChartBuilderOpen(false); setEditingChart(null); }}
        />
      )}

      {/* Settings modal */}
      {settingsOpen && (
        <div
          onClick={() => setSettingsOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 8, padding: 24,
              width: '90%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18, color: C.text }}>Customize dashboard</h2>
              <button
                onClick={() => setSettingsOpen(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.textMuted }}
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: C.textMuted }}>
              Toggle which widgets you want to see. Changes save automatically.
            </p>
            {WIDGET_DEFS
              .filter(w => !w.adminOnly || role === 'admin' || role === 'viewer')
              .map(w => (
                <label
                  key={w.key}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 0', borderBottom: `1px solid ${C.border}`,
                    cursor: 'pointer', fontSize: 14,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={widgetVisible(widgetPrefs, w.key)}
                    onChange={() => toggleWidget(w.key)}
                  />
                  <span>{w.label}</span>
                </label>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SavedChartCard — fetches its own data and renders the appropriate chart type
// ============================================================================
const SavedChartCard: React.FC<{
  chart: SavedChart;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ chart, onEdit, onDelete }) => {
  const api = useApi();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ChartData>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Metrics whose grouping dimension maps to a tickets-list filter → clickable.
  const DRILL_PARAM: Record<string, string> = {
    tickets_by_priority: 'priority',
    tickets_by_status: 'status',
    tickets_by_category: 'category',
    tickets_by_tech: 'assignee',
    tickets_by_requester: 'requester',
  };
  let drillParam: string | undefined;
  try { drillParam = DRILL_PARAM[(JSON.parse(chart.ConfigJson) as ChartConfig).metric]; } catch { /* ignore */ }
  const onDrill = drillParam
    ? (label: string) => navigate(`/tickets?${new URLSearchParams({ [drillParam as string]: label }).toString()}`)
    : undefined;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const cfg: ChartConfig = JSON.parse(chart.ConfigJson);
        const result = await api.post<{ rows: any[] }>('/api/reports/custom', {
          metric: cfg.metric,
          dateFrom: cfg.dateFrom,
          dateTo: cfg.dateTo,
          priorityFilter: cfg.priorityFilter,
          techFilter: cfg.techFilter,
        });
        if (!cancelled) setRows(Array.isArray(result.rows) ? result.rows : []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart.ChartId, chart.ConfigJson]);

  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface,
      padding: 14, position: 'relative',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 10, gap: 8,
      }}>
        <h4 style={{ margin: 0, fontSize: 14, color: C.text, flex: 1 }}>{chart.Title}</h4>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={onEdit}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: C.textMuted, fontSize: 11, padding: 4,
            }}
            title="Edit"
          >Edit</button>
          <button
            onClick={onDelete}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: C.danger, fontSize: 11, padding: 4,
            }}
            title="Delete"
          ><X size={12} /></button>
        </div>
      </div>
      <div style={{ minHeight: 220 }}>
        {loading ? (
          <div style={{ color: C.textMuted, fontSize: 13, padding: 40, textAlign: 'center' }}>Loading…</div>
        ) : error ? (
          <div style={{ color: C.danger, fontSize: 13, padding: 20 }}>Error: {error}</div>
        ) : rows.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: 13, padding: 40, textAlign: 'center' }}>
            No data for this query.
          </div>
        ) : (
          <ChartRenderer rows={rows} chartType={chart.ChartType} onDrill={onDrill} />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ChartRenderer — switches on chart type. Uses recharts for line/bar/pie.
// ============================================================================
const CHART_COLORS = ['#1e4d3a', '#7a9b7e', '#c4a25c', '#c4533a', '#3a73c4', '#7a3ac4', '#c43a93', '#3ac493', '#c4793a'];

const ChartRenderer: React.FC<{ rows: ChartData; chartType: 'line'|'bar'|'pie'|'kpi'; onDrill?: (label: string) => void }> = ({ rows, chartType, onDrill }) => {
  if (chartType === 'kpi') {
    const first = rows[0];
    const numeric = typeof first?.value === 'number' ? first.value : Number(first?.value ?? 0);
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: 24, minHeight: 200,
      }}>
        <div style={{
          fontSize: 48, fontWeight: 700, color: C.primary, lineHeight: 1,
        }}>
          {Number.isFinite(numeric) ? numeric.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: C.textMuted, textAlign: 'center' }}>
          {first?.label ?? ''}
        </div>
      </div>
    );
  }

  if (chartType === 'pie') {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie
            data={rows}
            dataKey="value"
            nameKey="label"
            cx="50%" cy="50%"
            outerRadius={80}
            label={({ label, percent }: any) => `${label} ${((percent ?? 0) * 100).toFixed(0)}%`}
            labelLine={false}
            style={{ cursor: onDrill ? 'pointer' : 'default' }}
            onClick={(d: any) => { const n = d?.label ?? d?.payload?.label; if (onDrill && n) onDrill(n); }}
          >
            {rows.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'line') {
    const hasResolved = rows.some(r => 'resolved' in r);
    return (
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="value" stroke={C.primary} strokeWidth={2} name="Count" />
          {hasResolved && <Line type="monotone" dataKey="resolved" stroke={C.ok} strokeWidth={2} name="Resolved" />}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // bar (default)
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={rows}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey="value" fill={C.primary} style={{ cursor: onDrill ? 'pointer' : 'default' }}
          onClick={(d: any) => { const n = d?.label ?? d?.payload?.label; if (onDrill && n) onDrill(n); }} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ============================================================================
// ChartBuilderModal — dropdown-based chart configuration
// ============================================================================
const ChartBuilderModal: React.FC<{
  editing: SavedChart | null;
  onSave: (title: string, chartType: 'line'|'bar'|'pie'|'kpi', config: ChartConfig) => Promise<void>;
  onClose: () => void;
}> = ({ editing, onSave, onClose }) => {
  const api = useApi();
  const narrow = useIsNarrow();
  // Defaults — either from the chart being edited, or fresh
  const initialCfg: ChartConfig = editing ? JSON.parse(editing.ConfigJson) : { metric: 'tickets_by_priority' };
  const [title, setTitle] = useState(editing?.Title ?? '');
  const [metric, setMetric] = useState(initialCfg.metric);
  const [chartType, setChartType] = useState<'line'|'bar'|'pie'|'kpi'>(
    editing?.ChartType ?? METRIC_OPTIONS.find(m => m.value === initialCfg.metric)?.defaultChart ?? 'bar'
  );
  const [dateFrom, setDateFrom] = useState(initialCfg.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(initialCfg.dateTo ?? '');
  const [priorityFilter, setPriorityFilter] = useState(initialCfg.priorityFilter ?? '');
  const [techFilter, setTechFilter] = useState<number | ''>(initialCfg.techFilter ?? '');
  const [techs, setTechs] = useState<{ UserId: number; DisplayName: string; Role: string }[]>([]);

  // Preview
  const [previewRows, setPreviewRows] = useState<ChartData>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Load tech list once
  useEffect(() => {
    (async () => {
      try {
        const u = await api.get<{ UserId: number; DisplayName: string; Role: string }[]>('/api/users');
        setTechs(Array.isArray(u) ? u.filter(x => x.Role === 'admin' || x.Role === 'tech') : []);
      } catch { /* ignore */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-update chart type when metric changes (only if user hasn't manually picked one)
  const onMetricChange = (newMetric: string) => {
    setMetric(newMetric);
    const def = METRIC_OPTIONS.find(m => m.value === newMetric);
    if (def) setChartType(def.defaultChart);
  };

  // Run a preview query
  const runPreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await api.post<{ rows: any[] }>('/api/reports/custom', {
        metric,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        priorityFilter: priorityFilter || undefined,
        techFilter: typeof techFilter === 'number' ? techFilter : undefined,
      });
      setPreviewRows(Array.isArray(result.rows) ? result.rows : []);
    } catch (e: any) { setPreviewError(e?.message ?? 'Failed'); }
    finally { setPreviewLoading(false); }
  };

  // Auto-preview when settings change
  useEffect(() => {
    runPreview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric, dateFrom, dateTo, priorityFilter, techFilter]);

  const handleSave = async () => {
    if (!title.trim()) { alert('Title required'); return; }
    setSaving(true);
    try {
      await onSave(title.trim(), chartType, {
        metric,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        priorityFilter: priorityFilter || undefined,
        techFilter: typeof techFilter === 'number' ? techFilter : undefined,
      });
    } catch (e: any) { alert(`Save failed: ${e?.message}`); }
    finally { setSaving(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 8, width: '100%', maxWidth: 900,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h3 style={{ margin: 0, fontSize: 16, color: C.primary }}>
            {editing ? 'Edit chart' : 'New chart'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}>
            <X size={18} />
          </button>
        </div>

        {/* Body — two columns: controls on left, preview on right (stacks on mobile) */}
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 280px) 1fr', flex: 1, overflow: narrow ? 'auto' : 'hidden' }}>
          {/* Controls */}
          <div style={{
            padding: 16, borderRight: `1px solid ${C.border}`,
            overflowY: 'auto', background: C.bg,
          }}>
            <Field label="Title">
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Open tickets by tech"
                autoFocus
                style={inputSty()}
              />
            </Field>

            <Field label="Metric (what to chart)">
              <select value={metric} onChange={e => onMetricChange(e.target.value)} style={inputSty()}>
                {METRIC_OPTIONS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Chart type">
              <div style={{ display: 'flex', gap: 4 }}>
                {(['bar', 'line', 'pie', 'kpi'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setChartType(t)}
                    style={{
                      flex: 1, padding: '6px 8px', fontSize: 11,
                      background: chartType === t ? C.primary : C.surface,
                      color: chartType === t ? 'white' : C.text,
                      border: `1px solid ${chartType === t ? C.primary : C.border}`,
                      borderRadius: 4, cursor: 'pointer', fontWeight: chartType === t ? 600 : 400,
                      textTransform: 'capitalize',
                    }}
                  >{t}</button>
                ))}
              </div>
            </Field>

            <div style={{ height: 1, background: C.border, margin: '14px 0' }} />

            <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Filters (optional)
            </div>

            <Field label="Date from">
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputSty()} />
            </Field>

            <Field label="Date to">
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputSty()} />
            </Field>

            <Field label="Priority">
              <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} style={inputSty()}>
                <option value="">Any</option>
                {['Critical', 'High', 'Medium', 'Low'].map(p => <option key={p}>{p}</option>)}
              </select>
            </Field>

            <Field label="Assigned tech">
              <select
                value={techFilter}
                onChange={e => setTechFilter(e.target.value ? parseInt(e.target.value) : '')}
                style={inputSty()}
              >
                <option value="">Any</option>
                {techs.map(t => <option key={t.UserId} value={t.UserId}>{t.DisplayName}</option>)}
              </select>
            </Field>
          </div>

          {/* Preview */}
          <div style={{ padding: 16, overflowY: 'auto' }}>
            <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Preview
            </div>
            <div style={{
              border: `1px solid ${C.border}`, borderRadius: 6, padding: 12,
              minHeight: 280, background: C.surface,
            }}>
              {previewLoading ? (
                <div style={{ color: C.textMuted, fontSize: 13, padding: 40, textAlign: 'center' }}>Loading…</div>
              ) : previewError ? (
                <div style={{ color: C.danger, fontSize: 13, padding: 20 }}>Error: {previewError}</div>
              ) : previewRows.length === 0 ? (
                <div style={{ color: C.textMuted, fontSize: 13, padding: 40, textAlign: 'center' }}>
                  No data for these filters.
                </div>
              ) : (
                <ChartRenderer rows={previewRows} chartType={chartType} />
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button onClick={onClose} disabled={saving} style={{
            background: 'transparent', border: `1px solid ${C.border}`,
            padding: '8px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer',
          }}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            style={{
              background: !title.trim() ? '#ccc' : C.primary, color: 'white',
              border: 'none', padding: '8px 18px', borderRadius: 4,
              fontSize: 13, fontWeight: 600,
              cursor: !title.trim() ? 'not-allowed' : 'pointer',
            }}
          >{saving ? 'Saving…' : editing ? 'Save changes' : 'Save chart'}</button>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 10 }}>
    <label style={{
      display: 'block', fontSize: 11, color: C.textMuted, fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4,
    }}>{label}</label>
    {children}
  </div>
);

const inputSty = (): React.CSSProperties => ({
  width: '100%', boxSizing: 'border-box',
  padding: '7px 10px', fontSize: 13,
  border: `1px solid ${C.border}`, borderRadius: 4,
  background: C.surface,
});
